import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import EditableSection from '../EditableSection';
import Toggle from '../ui/Toggle';
import { useEditableDraft } from '../../hooks/useEditableDraft';
import { useUpdateInvoiceBundleExtrasMutation } from '../../api/account/accountApiSlice';
import { useInvoiceMasterSlug } from '../../hooks/useIsInvoiceIssuingTenant';
import { enqueueSuccessSnackbar } from '../../action/snackbarCommands';
import { errorMessageFrom } from '../../utils/errorMessage';

interface Props {
    /** The tenant's ADR-0012 mode. Only `kolula` issues, so only it has the setting. */
    invoiceIssuer: string | null | undefined;
    /** The stored setting; absent reads as off. */
    invoiceBundleExtrasIntoStay: boolean;
    /** No edit rights: the view only, no "Bearbeiten". */
    readOnly?: boolean;
}

/**
 * "Zusatzleistungen in die Übernachtungsposition einrechnen" (#3427 item 7):
 * extras at the stay's VAT rate go into the stay line on issued invoices, as
 * the client-side invoice did for tenants that asked for it. Extras at another
 * rate keep their own line — the server decides which, by rate.
 *
 * One editable unit (design-decisions › Bearbeiten und Speichern), like the
 * dispatch address beside it.
 */
const InvoiceBundleExtrasSection = ({
    invoiceIssuer, invoiceBundleExtrasIntoStay, readOnly = false,
}: Readonly<Props>) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const masterSlug = useInvoiceMasterSlug();
    const [save, { isLoading: saving }] = useUpdateInvoiceBundleExtrasMutation();
    const draft = useEditableDraft<boolean>(invoiceBundleExtrasIntoStay);
    const [error, setError] = useState<string | null>(null);

    if (invoiceIssuer !== 'kolula') return null;

    const handleSave = async () => {
        setError(null);
        try {
            await save({
                invoiceBundleExtrasIntoStay: draft.value,
                ...(masterSlug ? { slug: masterSlug } : {}),
            }).unwrap();
            dispatch(enqueueSuccessSnackbar({ message: t('account.saved'), autoHideDuration: 3000 }));
            draft.finish();
        } catch (err: unknown) {
            setError(errorMessageFrom(err, t('account.invoice_bundle_save_failed'), t));
        }
    };

    return (
        <EditableSection
            title={t('account.invoice_bundle_section')}
            description={t('account.invoice_bundle_section_desc')}
            readOnly={readOnly}
            editing={draft.editing}
            isDirty={draft.isDirty}
            saving={saving}
            error={error}
            onEdit={draft.startEdit}
            onCancel={() => { setError(null); draft.cancel(); }}
            onSave={() => { void handleSave(); }}
            view={(
                <div className={'flex flex-col gap-1'}>
                    <span className={'text-sm font-medium text-foreground'}>{t('account.invoice_bundle_toggle')}</span>
                    <span className={'text-sm text-foreground/60'}>
                        {t(invoiceBundleExtrasIntoStay ? 'account.invoice_bundle_on' : 'account.invoice_bundle_off')}
                    </span>
                </div>
            )}
        >
            <Toggle
                checked={draft.value}
                onChange={draft.setDraft}
                label={t('account.invoice_bundle_toggle')}
                description={t('account.invoice_bundle_toggle_desc')}
                disabled={saving}
            />
        </EditableSection>
    );
};

export default InvoiceBundleExtrasSection;
